import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { DEFAULT_LAYOUT, sanitizeLayout, type WidgetId } from "@/lib/dashboard-widgets";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const QUERY_KEY = ["dashboard-layout"];
// One fixed toast id: rapid reorders replace the toast instead of stacking.
const TOAST_ID = "dashboard-layout";

/**
 * The caller's saved tail layout (POS2-877): fetched once, PATCHed on every
 * mutation with an optimistic cache write so reorders feel instant (rolled back
 * with an error toast on failure). Null from the API = the default order.
 */
export function useDashboardLayout() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  const query = useQuery<WidgetId[] | null>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/users/me/preferences`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: { dashboard_layout: unknown } };
      return sanitizeLayout(body.data.dashboard_layout);
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (layout: WidgetId[]) => {
      const res = await fetch(`${API_URL}/api/v1/users/me/preferences`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ dashboard_layout: layout }),
      });
      if (!res.ok) throw await apiError(t, res);
    },
  });

  const { mutate } = mutation;
  const save = useCallback(
    (next: WidgetId[], message: string) => {
      const previous = queryClient.getQueryData<WidgetId[] | null>(QUERY_KEY) ?? null;
      queryClient.setQueryData(QUERY_KEY, next);
      mutate(next, {
        onSuccess: () => toast.success(message, { id: TOAST_ID }),
        onError: () => {
          queryClient.setQueryData(QUERY_KEY, previous);
          toast.error(t("settings.layoutSaveFailed"), { id: TOAST_ID });
        },
      });
    },
    [queryClient, mutate, t],
  );

  // undefined (loading) and null (no saved layout) both mean the default order;
  // an empty array is a saved "removed everything" state and passes through.
  const layout: readonly WidgetId[] = query.data ?? DEFAULT_LAYOUT;

  return { layout, isLoading: query.isLoading, save };
}
